import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeft, Save, Loader2, User, Briefcase, Home, CreditCard, Shield, FileText,
  FileCheck, GraduationCap, Monitor, Link2, MessagesSquare, FileSignature, FolderOpen,
} from 'lucide-react';

import {
  BankDetailsTab, PersonalTab, AddressTab, EmploymentTab, ComplianceTab,
  TrainingsTab, QualificationsTab, ContractsTab, OtherTab, ITAccessTab, SystemAccessTab,
  DocumentsTab,
} from './employee';
import MonthlyHoursEditor from './employee/MonthlyHoursEditor';
import { useEmployeeForm } from './employee/useEmployeeForm';
import { useITAccess } from './employee/useITAccess';
import { useSystemAccess } from './employee/useSystemAccess';
import EmployeeNotesTab from './EmployeeNotesTab';

export default function EmployeeForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const { isAdmin, hasPermission } = useAuth();
  const canViewSensitive = isAdmin || hasPermission('sensitive_data', 'view');
  // Gespraechsnotizen-Tab nur fuer User mit `employees:view` (Standortleiter+).
  // Hard-Rule: ein Mitarbeitender, der das eigene Profil bearbeitet, sieht den
  // Tab NICHT (Backend blockiert ihn ebenfalls).
  const canEditNotes = isAdmin || hasPermission('employees', 'edit') || hasPermission('employees', 'view');

  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const initialTab = tabFromUrl && (tabFromUrl !== 'notes' || canEditNotes) ? tabFromUrl : 'personal';
  const [activeTab, setActiveTab] = useState(initialTab);

  // Tab via URL umschaltbar (z.B. „Gesprächsnotiz"-Quick-Button)
  useEffect(() => {
    if (tabFromUrl && tabFromUrl !== activeTab) {
      if (tabFromUrl === 'notes' && !canEditNotes) return;
      setActiveTab(tabFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabFromUrl]);

  // Bei jedem Wechsel den Tab ins URL spiegeln (Browser-Verlauf-freundlich)
  const handleTabChange = (value) => {
    setActiveTab(value);
    const next = new URLSearchParams(searchParams);
    if (value === 'personal') next.delete('tab');
    else next.set('tab', value);
    setSearchParams(next, { replace: true });
  };

  // System access (needs business_email from form - passed at call time)
  const sys = useSystemAccess({ employeeId: id, isEdit });

  // Form state — depends on linkedUser + userPermissions for permission auto-save on submit
  const form = useEmployeeForm({
    employeeId: id,
    isEdit,
    linkedUser: sys.linkedUser,
    userPermissions: sys.userPermissions,
  });

  // IT access
  const it = useITAccess({ employeeId: id, isEdit });

  if (form.loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-[600px] rounded-xl" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="employee-form-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/employees')} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="font-heading font-bold text-3xl text-foreground">
              {isEdit ? 'Mitarbeiter bearbeiten' : 'Neuer Mitarbeiter'}
            </h1>
            {isEdit && (
              <p className="text-muted-foreground mt-1">
                {[form.formData.last_name, form.formData.first_name].filter(Boolean).join(', ') || 'Mitarbeiter'}
                {form.formData.employee_number ? ` · ${form.formData.employee_number}` : ''}
              </p>
            )}
          </div>
        </div>

        <form onSubmit={form.handleSubmit}>
          <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
            <TabsList className="bg-secondary/50 flex-wrap h-auto gap-1 p-1">
              <TabsTrigger value="personal" className="gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3">
                <User className="h-4 w-4" /> <span className="hidden sm:inline">Persönlich</span>
              </TabsTrigger>
              <TabsTrigger value="address" className="gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3">
                <Home className="h-4 w-4" /> <span className="hidden sm:inline">Adresse</span>
              </TabsTrigger>
              <TabsTrigger value="employment" className="gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3">
                <Briefcase className="h-4 w-4" /> <span className="hidden sm:inline">Beschäftigung</span>
              </TabsTrigger>
              {canViewSensitive && (
                <TabsTrigger value="bank" className="gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3" data-testid="tab-bank">
                  <CreditCard className="h-4 w-4" /> <span className="hidden sm:inline">Bank</span>
                </TabsTrigger>
              )}
              {canViewSensitive && (
                <TabsTrigger value="compliance" className="gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3" data-testid="tab-compliance">
                  <FileCheck className="h-4 w-4" /> <span className="hidden sm:inline">Compliance</span>
                </TabsTrigger>
              )}
              {canViewSensitive && (
                <TabsTrigger value="contracts" className="gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3" data-testid="tab-contracts">
                  <FileSignature className="h-4 w-4" /> <span className="hidden sm:inline">Verträge</span>
                </TabsTrigger>
              )}
              {canViewSensitive && isEdit && (
                <TabsTrigger value="documents" className="gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3" data-testid="tab-documents">
                  <FolderOpen className="h-4 w-4" /> <span className="hidden sm:inline">Dokumente</span>
                </TabsTrigger>
              )}
              <TabsTrigger value="trainings" className="gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3">
                <GraduationCap className="h-4 w-4" /> <span className="hidden sm:inline">Weiterbildung</span>
              </TabsTrigger>
              <TabsTrigger value="qualifications" className="gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3">
                <Shield className="h-4 w-4" /> <span className="hidden sm:inline">Qualifikationen</span>
              </TabsTrigger>
              <TabsTrigger value="other" className="gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3">
                <FileText className="h-4 w-4" /> <span className="hidden sm:inline">Sonstiges</span>
              </TabsTrigger>
              {isEdit && canEditNotes && (
                <TabsTrigger value="notes" className="gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3" data-testid="tab-notes">
                  <MessagesSquare className="h-4 w-4" /> <span className="hidden sm:inline">Gespräche</span>
                </TabsTrigger>
              )}
              {isEdit && (
                <>
                  <TabsTrigger value="it" className="gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3">
                    <Monitor className="h-4 w-4" /> <span className="hidden sm:inline">IT</span>
                  </TabsTrigger>
                  <TabsTrigger value="system" className="gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3">
                    <Link2 className="h-4 w-4" /> <span className="hidden sm:inline">System</span>
                  </TabsTrigger>
                </>
              )}
            </TabsList>

            <TabsContent value="personal">
              <PersonalTab formData={form.formData} handleChange={form.handleChange} isEdit={isEdit} />
            </TabsContent>

            <TabsContent value="address">
              <AddressTab formData={form.formData} handleChange={form.handleChange} />
            </TabsContent>

            <TabsContent value="employment">
              <EmploymentTab
                formData={form.formData}
                handleChange={form.handleChange}
                locations={form.locations}
                branches={form.branches}
                orgTree={form.orgTree}
                orgLevels={form.orgLevels}
              />
              {isEdit && form.formData.working_time_model === 'monthly' && (
                <MonthlyHoursEditor
                  employeeId={id}
                  weeklyHours={form.formData.weekly_hours || 39}
                />
              )}
            </TabsContent>

            {canViewSensitive && (
              <TabsContent value="bank">
                <BankDetailsTab formData={form.formData} handleChange={form.handleChange} />
              </TabsContent>
            )}

            {canViewSensitive && (
              <TabsContent value="compliance">
                <ComplianceTab
                  formData={form.formData}
                  handlePoliceCheckChange={form.handlePoliceCheckChange}
                  handleMeaslesChange={form.handleMeaslesChange}
                  isMeaslesRequired={form.isMeaslesRequired}
                />
              </TabsContent>
            )}

            {canViewSensitive && (
              <TabsContent value="contracts">
                <ContractsTab
                  formData={form.formData}
                  employeeId={id}
                  newContract={form.newContract}
                  setNewContract={form.setNewContract}
                  addContract={form.addContract}
                  removeContract={form.removeContract}
                  handleContractFileUpload={form.handleContractFileUpload}
                  uploadingContract={form.uploadingContract}
                />
              </TabsContent>
            )}


            {canViewSensitive && isEdit && (
              <TabsContent value="documents">
                <DocumentsTab employeeId={id} employeeName={`${form.formData?.first_name || ''} ${form.formData?.last_name || ''}`.trim()} />
              </TabsContent>
            )}

            <TabsContent value="trainings">
              <TrainingsTab
                formData={form.formData}
                newTraining={form.newTraining}
                setNewTraining={form.setNewTraining}
                addTraining={form.addTraining}
                removeTraining={form.removeTraining}
                handleCertificateUpload={form.handleCertificateUpload}
                uploading={form.uploading}
                newCertification={form.newCertification}
                setNewCertification={form.setNewCertification}
                addCertification={form.addCertification}
                removeCertification={form.removeCertification}
              />
            </TabsContent>

            <TabsContent value="qualifications">
              <QualificationsTab
                formData={form.formData}
                setFormData={form.setFormData}
                newQualification={form.newQualification}
                setNewQualification={form.setNewQualification}
                addQualification={form.addQualification}
                removeQualification={form.removeQualification}
                handleQualificationFileUpload={form.handleQualificationFileUpload}
                uploadingQualification={form.uploadingQualification}
              />
            </TabsContent>

            <TabsContent value="other">
              <OtherTab
                formData={form.formData}
                handleChange={form.handleChange}
                handleEmergencyContactChange={form.handleEmergencyContactChange}
                newDocument={form.newDocument}
                setNewDocument={form.setNewDocument}
                handleDocumentUpload={form.handleDocumentUpload}
                addDocument={form.addDocument}
                removeDocument={form.removeDocument}
                uploadingDocument={form.uploadingDocument}
              />
            </TabsContent>

            {isEdit && canEditNotes && (
              <TabsContent value="notes">
                <EmployeeNotesTab
                  employeeId={id}
                  canEdit={isAdmin || hasPermission('employees', 'edit')}
                />
              </TabsContent>
            )}

            {isEdit && (
              <TabsContent value="it">
                <ITAccessTab
                  formData={form.formData}
                  itPresets={it.itPresets}
                  presetDialogOpen={it.presetDialogOpen}
                  setPresetDialogOpen={it.setPresetDialogOpen}
                  newPreset={it.newPreset}
                  setNewPreset={it.setNewPreset}
                  editingPreset={it.editingPreset}
                  setEditingPreset={it.setEditingPreset}
                  handleSavePreset={it.handleSavePreset}
                  handleDeletePreset={it.handleDeletePreset}
                  handleAddFromPreset={it.handleAddFromPreset}
                  itAccesses={it.itAccesses}
                  loadingIT={it.loadingIT}
                  newITAccess={it.newITAccess}
                  setNewITAccess={it.setNewITAccess}
                  editingITAccess={it.editingITAccess}
                  setEditingITAccess={it.setEditingITAccess}
                  handleAddITAccess={it.handleAddITAccess}
                  handleUpdateITAccess={it.handleUpdateITAccess}
                  handleDeleteITAccess={it.handleDeleteITAccess}
                  showPasswords={it.showPasswords}
                  setShowPasswords={it.setShowPasswords}
                />
              </TabsContent>
            )}

            {isEdit && (
              <TabsContent value="system">
                <SystemAccessTab
                  formData={form.formData}
                  handleChange={form.handleChange}
                  linkedUser={sys.linkedUser}
                  linkingUser={sys.linkingUser}
                  creatingAccess={sys.creatingAccess}
                  handleRemoveSystemAccess={sys.handleRemoveSystemAccess}
                  handleCreateSystemAccess={() => sys.handleCreateSystemAccess(form.formData.business_email)}
                  userPermissions={sys.userPermissions}
                  handlePermissionChange={sys.handlePermissionChange}
                  handleRoleSelect={sys.handleRoleSelect}
                  roles={sys.roles}
                  branches={form.branches}
                  locations={form.locations}
                  savingPermissions={sys.savingPermissions}
                  handleSavePermissions={sys.handleSavePermissions}
                  newPassword={sys.newPassword}
                  setNewPassword={sys.setNewPassword}
                  showNewPassword={sys.showNewPassword}
                  setShowNewPassword={sys.setShowNewPassword}
                  generateNewPassword={sys.generatePassword}
                />
              </TabsContent>
            )}
          </Tabs>

          <div className="flex justify-end gap-4 pt-6">
            <Button type="button" variant="outline" onClick={() => navigate('/employees')} className="rounded-full" data-testid="cancel-btn">
              Abbrechen
            </Button>
            <Button type="submit" disabled={form.saving} className="rounded-full" data-testid="save-employee-btn">
              {form.saving ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Speichern...</>
              ) : (
                <><Save className="mr-2 h-4 w-4" />{isEdit ? 'Aktualisieren' : 'Erstellen'}</>
              )}
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}
